import { Modal, Text, Divider, Loader } from "@mantine/core";
import { Badge } from "@xplatform/design-system";
import type { EvaluationDetailViewModel } from "@models/evaluation";
import { EvaluationDetailCard } from "../EvaluationDetailCard";
import { formatAccuracy, getAccuracyColor } from "@utils/evaluationFormatters";
import "./EvaluationDetailDialog.scss";

interface EvaluationDetailDialogProps {
    open: boolean;
    onClose: () => void;
    processId: string;
    evaluationDetails: EvaluationDetailViewModel[];
    isLoading: boolean;
}

export const EvaluationDetailDialog = ({
    open,
    onClose,
    processId,
    evaluationDetails,
    isLoading,
}: EvaluationDetailDialogProps) => {
    const avgAccuracy =
        evaluationDetails.length > 0
            ? evaluationDetails.reduce((sum, detail) => sum + detail.accuracy, 0) /
            evaluationDetails.length
            : 0;

    return (
        <Modal
            opened={open}
            onClose={onClose}
            size="60%"
            title={
                <div className="evaluation-detail-dialog__title-content">
                    <Text size="lg" fw={600}>
                        Evaluation Details
                    </Text>
                    <Text size="sm" c="dimmed">
                        Process ID: {processId}
                    </Text>
                </div>
            }
            className="evaluation-detail-dialog"
            portalProps={{ target: "#admin-portal-root" }}
            styles={{
                content: {
                    maxHeight: "calc(100vh - 80px)",
                    display: "flex",
                    flexDirection: "column",
                },
                header: {
                    flexShrink: 0,
                },
                body: {
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    overflow: "hidden",
                },
            }}
        >
            <Divider mb="md" />

            <div className="evaluation-detail-dialog__scroll-content">
                {isLoading ? (
                    <div className="evaluation-detail-dialog__loading">
                        <Loader />
                        <Text size="sm" c="dimmed">
                            Loading evaluation details...
                        </Text>
                    </div>
                ) : evaluationDetails.length > 0 ? (
                    <>
                        <div className="evaluation-detail-dialog__summary">
                            <Text size="sm" fw={600} c="dimmed">
                                Overall Accuracy
                            </Text>
                            <Badge color={getAccuracyColor(avgAccuracy)} size="lg">
                                {formatAccuracy(avgAccuracy)}
                            </Badge>
                        </div>

                        <div className="evaluation-detail-dialog__list">
                            {evaluationDetails.map((detail) => (
                                <EvaluationDetailCard key={detail.id} detail={detail} />
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="evaluation-detail-dialog__empty">
                        <Text size="md" c="dimmed">
                            No evaluation details available
                        </Text>
                    </div>
                )}
            </div>
        </Modal>
    );
};
